import { InternalTooltip, useDefaultTooltipConnector } from 'react-rainbow-components';
import { useRef, useState, useEffect } from 'react';
import listTags from 'data/firestore/agent/tag/list';
import {
    BadgeContainer,
    TooltipContent,
    TagHeader,
    TagDescription,
    LoadingContainer,
    LoadingBar,
    LoadingBarMedium,
    TagIcon,
} from './styled';
import Badge from '../Badge';

interface Props {
    tag: string;
    agentId: string;
    entity: 'customer' | 'call' | 'opportunity' | 'workspace';
    onClick?: () => void;
}

const fetchTagDescription = async (
    tagName: string,
    agentId: string,
    entity: string,
): Promise<string> => {
    const tags = await listTags(agentId);
    const tag = tags.find((t) => t.name === tagName && t.entity === entity);
    return tag?.description || 'The tag has no description';
};

const TagBadge = ({
    tag,
    agentId,
    entity,
    onClick = () => {},
}: Props) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef();
    const [isLoading, setIsLoading] = useState(false);
    const [description, setDescription] = useState<string | null>(null);
    const {
        onMouseEnter,
        onMouseLeave,
        isVisible,
    } = useDefaultTooltipConnector({
        tooltipRef,
        triggerRef: () => containerRef as any,
    });

    useEffect(() => {
        const fetchDescription = async () => {
            if (isVisible && !description && !isLoading) {
                setIsLoading(true);
                try {
                    const desc = await fetchTagDescription(tag, agentId, entity);
                    setDescription(desc);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchDescription();
    }, [isVisible, tag, description, isLoading, agentId, entity]);

    return (
        <BadgeContainer
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={onClick}
            ref={containerRef}
        >
            <Badge variant="outline-brand" borderRadius="semi-rounded">
                <TagIcon />
                {tag}
            </Badge>
            <InternalTooltip
                triggerElementRef={
                    () => containerRef
                }
                isVisible={isVisible}
                preferredPosition="top"
                ref={tooltipRef}
            >
                <TooltipContent>
                    <TagHeader>{tag}</TagHeader>
                    {isLoading ? (
                        <LoadingContainer>
                            <LoadingBar />
                            <LoadingBarMedium />
                        </LoadingContainer>
                    ) : (
                        <TagDescription>{description || 'No description available'}</TagDescription>
                    )}
                </TooltipContent>
            </InternalTooltip>
        </BadgeContainer>
    );
};

export default TagBadge;
